import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Receipt as InvoiceIcon,
  LocalShipping as DeliveryIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Payment as PaymentIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { paymentService } from '../../../services/paymentService';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';

import { salesOrderService } from '../../../services/salesOrderService';
import { SalesOrder, SalesOrderStatus, SALES_ORDER_STATUS_COLORS } from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/formatters';
import DocumentAuditHistory from '../../../components/audit/DocumentAuditHistory';
import DocumentNotesTimeline from '../../../components/documents/DocumentNotesTimeline';
import { DocumentContextMenu } from '../../../components/documents/DocumentContextMenu';
import SalesOrderPDFPreview from '../../../components/sales-orders/SalesOrderPDFPreview';
import ShipmentSection from '../../../components/sales-orders/ShipmentSection';

const SalesOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [salesOrder, setSalesOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [convertToInvoiceOpen, setConvertToInvoiceOpen] = useState(false);
  const [dueDays, setDueDays] = useState(30);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<SalesOrderStatus>(SalesOrderStatus.CONFIRMED);
  const [statusNotes, setStatusNotes] = useState('');

  // Record Payment dialog state
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [recordPaymentLoading, setRecordPaymentLoading] = useState(false);
  const [recordPaymentForm, setRecordPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    amount: 0,
    reference_number: '',
    bank_name: '',
    notes: '',
  });

  // Generate Payment Link dialog state
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
  const [paymentLinkExpiryHours, setPaymentLinkExpiryHours] = useState(48);
  const [generatedLinkResult, setGeneratedLinkResult] = useState<any>(null);

  // Razorpay link status state
  const [razorpayLinkStatus, setRazorpayLinkStatus] = useState<{ status: string; paid_amount?: number; payment_link_url?: string } | null>(null);
  const [razorpayLinkStatusLoading, setRazorpayLinkStatusLoading] = useState(false);

  // Fetch sales order data
  useEffect(() => {
    const fetchSalesOrder = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const data = await salesOrderService.getSalesOrder(id);
        setSalesOrder(data);
      } catch (error: any) {
        console.error('Failed to fetch sales order:', error);
        setError(error?.response?.data?.detail || 'Failed to load sales order');
      } finally {
        setLoading(false);
      }
    };

    fetchSalesOrder();
  }, [id]);

  const getStatusColor = useCallback((status: SalesOrderStatus) => {
    return SALES_ORDER_STATUS_COLORS[status] || 'default';
  }, []);

  const permissions = useMemo(() => {
    if (!salesOrder) return {
      canEdit: false,
      canConfirm: false,
      canProcess: false,
      canReadyToShip: false,
      canShip: false,
      canCancel: false,
      canConvertToInvoice: false,
      canCreateDeliveryChallan: false
    };

    return {
      canEdit: [SalesOrderStatus.DRAFT, SalesOrderStatus.CONFIRMED].includes(salesOrder.status),
      canConfirm: salesOrder.status === SalesOrderStatus.DRAFT,
      canProcess: salesOrder.status === SalesOrderStatus.CONFIRMED,
      canReadyToShip: salesOrder.status === SalesOrderStatus.PROCESSING,
      canShip: salesOrder.status === SalesOrderStatus.READY_TO_SHIP,
      canCancel: ![SalesOrderStatus.CANCELLED, SalesOrderStatus.INVOICED].includes(salesOrder.status),
      canConvertToInvoice: [
        SalesOrderStatus.CONFIRMED,
        SalesOrderStatus.PROCESSING,
        SalesOrderStatus.READY_TO_SHIP,
        SalesOrderStatus.SHIPPED
      ].includes(salesOrder.status),
      canCreateDeliveryChallan: [
        SalesOrderStatus.CONFIRMED,
        SalesOrderStatus.PROCESSING,
        SalesOrderStatus.READY_TO_SHIP,
        SalesOrderStatus.INVOICED
      ].includes(salesOrder.status)
    };
  }, [salesOrder?.status]);

  // Valid status transitions based on current status
  const validStatusTransitions = useMemo(() => {
    if (!salesOrder) return [];
    
    const transitions: Record<SalesOrderStatus, SalesOrderStatus[]> = {
      [SalesOrderStatus.DRAFT]: [SalesOrderStatus.CONFIRMED, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.CONFIRMED]: [SalesOrderStatus.PROCESSING, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.PROCESSING]: [SalesOrderStatus.READY_TO_SHIP, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.READY_TO_SHIP]: [SalesOrderStatus.SHIPPED, SalesOrderStatus.CANCELLED],
      [SalesOrderStatus.SHIPPED]: [SalesOrderStatus.DELIVERED],
      [SalesOrderStatus.DELIVERED]: [],
      [SalesOrderStatus.INVOICED]: [],
      [SalesOrderStatus.CANCELLED]: [],
    };
    
    return transitions[salesOrder.status] || [];
  }, [salesOrder?.status]);

  const handleEdit = () => {
    navigate(`/admin/documents/sales-orders/${id}/edit`);
  };

  const handleDownloadPDF = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const blob = await salesOrderService.downloadPDF(salesOrder.id);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${salesOrder.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('PDF downloaded successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to download PDF:', error);
      enqueueSnackbar('Failed to download PDF', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.confirmSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order confirmed successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to confirm sales order:', error);
      
      // Handle insufficient stock error with details
      if (error.response?.data?.detail?.error === 'INSUFFICIENT_STOCK') {
        const insufficientItems = error.response.data.detail.insufficient_items || [];
        
        if (insufficientItems.length > 0) {
          // Create a user-friendly message
          const itemsList = insufficientItems
            .map((item: any) => {
              const productName = item.product_name || 'Unknown Product';
              const variantName = item.variant_name || item.sku || 'N/A';
              const required = item.required_quantity || item.requested_quantity || 0;
              const available = item.available_quantity || 0;
              const short = item.shortfall || (required - available);
              
              return `• ${productName} (${variantName}): Need ${required} units, only ${available} available (short by ${short})`;
            })
            .join('\n');
          
          enqueueSnackbar(
            `Cannot confirm order due to insufficient stock:\n\n${itemsList}\n\nPlease adjust quantities or wait for stock replenishment.`,
            { 
              variant: 'error',
              autoHideDuration: 15000,
              style: { whiteSpace: 'pre-line', maxWidth: '600px' }
            }
          );
        } else {
          enqueueSnackbar('Cannot confirm order - Insufficient stock for one or more items', { variant: 'error' });
        }
      } else if (error.response?.data?.detail?.code === 'INVALID_STATUS_TRANSITION') {
        const detail = error.response.data.detail;
        enqueueSnackbar(
          `Cannot change status: ${detail.message || 'Invalid status transition'}`,
          { variant: 'error' }
        );
      } else if (error.response?.data?.detail?.message) {
        enqueueSnackbar(error.response.data.detail.message, { variant: 'error' });
      } else if (error.response?.data?.detail) {
        const detail = typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail);
        enqueueSnackbar(detail, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to confirm sales order. Please try again.', { variant: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.PROCESSING,
        notes: 'Order moved to processing'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order moved to processing', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReadyToShip = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.READY_TO_SHIP,
        notes: 'Order ready to ship'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order marked as ready to ship', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleShip = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: SalesOrderStatus.SHIPPED,
        notes: 'Order shipped'
      });
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order marked as shipped', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.cancelSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      enqueueSnackbar('Sales order cancelled successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to cancel sales order:', error);
      enqueueSnackbar('Failed to cancel sales order', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const result = await salesOrderService.convertToInvoice(salesOrder.id, dueDays);
      
      enqueueSnackbar(`Invoice ${result.invoice_number} created successfully`, { variant: 'success' });
      setConvertToInvoiceOpen(false);
      
      // Refresh the sales order to show updated status
      const updatedOrder = await salesOrderService.getSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
      
      // Navigate to invoice detail page
      navigate(`/admin/documents/invoices/${result.invoice_id}`);
    } catch (error: any) {
      console.error('Failed to convert to invoice:', error);
      enqueueSnackbar('Failed to convert to invoice', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!salesOrder) return;

    try {
      setActionLoading(true);
      const updatedOrder = await salesOrderService.updateStatus(salesOrder.id, {
        status: newStatus,
        notes: statusNotes
      });
      setSalesOrder(updatedOrder);
      setStatusUpdateOpen(false);
      setStatusNotes('');
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateDeliveryChallan = () => {
    // Navigate to delivery challan creation page with sales order ID
    navigate(`/admin/documents/delivery-challans/create?sales_order_id=${salesOrder?.id}`);
  };

  const handleOpenRecordPayment = () => {
    setRecordPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'cash',
      amount: salesOrder ? Number(salesOrder.total_amount) : 0,
      reference_number: '',
      bank_name: '',
      notes: `Payment for Sales Order ${salesOrder?.order_number}`,
    });
    setRecordPaymentOpen(true);
  };

  const handleSubmitRecordPayment = async () => {
    if (!salesOrder) return;
    if (recordPaymentForm.amount <= 0) {
      enqueueSnackbar('Payment amount must be greater than 0', { variant: 'warning' });
      return;
    }
    try {
      setRecordPaymentLoading(true);
      if (salesOrder.converted_to_invoice_id) {
        await paymentService.recordInvoicePayment({
          invoice_id: salesOrder.converted_to_invoice_id,
          payment_date: recordPaymentForm.payment_date,
          payment_method: recordPaymentForm.payment_method,
          amount: recordPaymentForm.amount,
          reference_number: recordPaymentForm.reference_number || undefined,
          bank_name: recordPaymentForm.bank_name || undefined,
          notes: recordPaymentForm.notes || undefined,
        });
      } else {
        const result = await paymentService.recordAdvancePayment(salesOrder.id, {
          amount: recordPaymentForm.amount,
          payment_method: recordPaymentForm.payment_method,
          payment_date: recordPaymentForm.payment_date,
          reference_number: recordPaymentForm.reference_number || undefined,
          bank_name: recordPaymentForm.bank_name || undefined,
          notes: recordPaymentForm.notes || undefined,
        });
        if (result.auto_created_invoice) {
          enqueueSnackbar('Draft invoice auto-created for advance payment.', { variant: 'info' });
        }
      }
      enqueueSnackbar('Payment recorded successfully!', { variant: 'success' });
      setRecordPaymentOpen(false);
      const updatedOrder = await salesOrderService.getSalesOrder(salesOrder.id);
      setSalesOrder(updatedOrder);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to record payment', { variant: 'error' });
    } finally {
      setRecordPaymentLoading(false);
    }
  };

  const handleRefreshPaymentLinkStatus = async () => {
    if (!salesOrder?.converted_to_invoice_id) return;
    try {
      setRazorpayLinkStatusLoading(true);
      const status = await paymentService.getPaymentLinkStatus(salesOrder.converted_to_invoice_id);
      setRazorpayLinkStatus(status);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to fetch payment link status', { variant: 'error' });
    } finally {
      setRazorpayLinkStatusLoading(false);
    }
  };

  const handleOpenPaymentLink = () => {
    setGeneratedLinkResult(null);
    setPaymentLinkExpiryHours(48);
    setPaymentLinkOpen(true);
  };

  const handleGeneratePaymentLink = async () => {
    if (!salesOrder?.converted_to_invoice_id) return;
    try {
      setPaymentLinkLoading(true);
      const res = await paymentService.generatePaymentLink({
        invoice_id: salesOrder.converted_to_invoice_id,
        custom_expiry_hours: paymentLinkExpiryHours,
      });
      setGeneratedLinkResult(res);
      enqueueSnackbar('Razorpay payment link generated successfully!', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to generate payment link', { variant: 'error' });
    } finally {
      setPaymentLinkLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' 
      ? error 
      : error?.response?.data?.detail 
      ? (Array.isArray(error.response.data.detail) 
          ? error.response.data.detail[0]?.msg || 'An error occurred'
          : error.response.data.detail)
      : error?.message || 'Failed to load sales order';
    
    return (
      <Box>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  if (!salesOrder) {
    return (
      <Box>
        <Alert severity="error">Sales order not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/sales-orders">
          Sales Orders
        </Link>
        <Typography color="text.primary">
          {salesOrder.order_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/sales-orders')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Sales Order {salesOrder.order_number}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={salesOrder.status.toUpperCase()}
                color={getStatusColor(salesOrder.status) as any}
                size="small"
                clickable={salesOrder.status === SalesOrderStatus.INVOICED && !!salesOrder.converted_to_invoice_id}
                onClick={salesOrder.status === SalesOrderStatus.INVOICED && salesOrder.converted_to_invoice_id 
                  ? () => navigate(`/admin/documents/invoices/${salesOrder.converted_to_invoice_id}`)
                  : undefined
                }
              />
              {salesOrder.estimate && (
                <Tooltip title="Converted from estimate">
                  <Chip
                    label={`From ${salesOrder.estimate.estimate_number}`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/admin/documents/estimates/${salesOrder.estimate?.id}`)}
                  />
                </Tooltip>
              )}
              {salesOrder.invoice && (
                <Tooltip title="Converted to invoice">
                  <Chip
                    label={salesOrder.invoice.invoice_number}
                    color="success"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/admin/documents/invoices/${salesOrder.invoice.id}`)}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={handleDownloadPDF}
            disabled={actionLoading}
          >
            PDF Preview
          </Button>
          
          {permissions.canEdit && (
            <Tooltip
              title={
                salesOrder.status === SalesOrderStatus.CONFIRMED
                  ? 'Editing will release the current stock reservation and re-reserve stock for the new items'
                  : ''
              }
            >
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
            </Tooltip>
          )}
          
          {permissions.canConfirm && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ConfirmIcon />}
              onClick={handleConfirm}
              disabled={actionLoading}
            >
              Confirm Order
            </Button>
          )}
          
          {permissions.canProcess && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleProcess}
              disabled={actionLoading}
            >
              Start Processing
            </Button>
          )}
          
          {permissions.canReadyToShip && (
            <Button
              variant="contained"
              color="info"
              onClick={handleReadyToShip}
              disabled={actionLoading}
            >
              Ready to Ship
            </Button>
          )}
          
          {permissions.canShip && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DeliveryIcon />}
              onClick={handleShip}
              disabled={actionLoading}
            >
              Mark as Shipped
            </Button>
          )}
          
          {permissions.canCreateDeliveryChallan && (
            <Button
              variant="outlined"
              startIcon={<DeliveryIcon />}
              onClick={handleCreateDeliveryChallan}
            >
              Create Delivery Challan
            </Button>
          )}
          
          {permissions.canConvertToInvoice && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<InvoiceIcon />}
              onClick={() => setConvertToInvoiceOpen(true)}
              disabled={actionLoading}
            >
              Convert to Invoice
            </Button>
          )}

          <Button
            variant="outlined"
            color="success"
            startIcon={<PaymentIcon />}
            onClick={handleOpenRecordPayment}
          >
            Record Payment
          </Button>

          {salesOrder.converted_to_invoice_id && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<LinkIcon />}
              onClick={handleOpenPaymentLink}
            >
              Payment Link
            </Button>
          )}

          <DocumentContextMenu
            documentId={salesOrder.id}
            documentType="sales_order"
            documentNumber={salesOrder.order_number}
            customerEmail={salesOrder.customer?.email}
            customerPhone={salesOrder.customer?.phone}
          />
          
          {permissions.canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              disabled={actionLoading}
            >
              Cancel
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Sales Order Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {salesOrder.order_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(salesOrder.order_date), 'dd/MM/yyyy')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Expected Delivery
                  </Typography>
                  <Typography variant="body1">
                    {salesOrder.expected_delivery_date 
                      ? format(new Date(salesOrder.expected_delivery_date), 'dd/MM/yyyy')
                      : 'Not specified'
                    }
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={salesOrder.status.toUpperCase()}
                      color={getStatusColor(salesOrder.status) as any}
                      size="small"
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        if (validStatusTransitions.length > 0) {
                          setNewStatus(validStatusTransitions[0]);
                        }
                        setStatusUpdateOpen(true);
                      }}
                    >
                      Update
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Status
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Chip
                      label={(salesOrder.payment_status || 'PENDING').toUpperCase()}
                      color={
                        ['PAID', 'COMPLETED'].includes((salesOrder.payment_status || '').toUpperCase())
                          ? 'success'
                          : ['FAILED'].includes((salesOrder.payment_status || '').toUpperCase())
                          ? 'error'
                          : 'warning'
                      }
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Grid>
                {salesOrder.razorpay_order_id && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Gateway Ref
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 1 }}>
                      {salesOrder.razorpay_order_id}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Fulfillment Warehouse
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {salesOrder.warehouse?.name || 'Not assigned'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {salesOrder.customer?.billing_name || salesOrder.customer?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact Person: {salesOrder.customer?.contact_person || 'N/A'}
                  </Typography>
                  {salesOrder.customer?.email && (
                    <Typography variant="body2" color="text.secondary">
                      Email: {salesOrder.customer.email}
                    </Typography>
                  )}
                  {(salesOrder.customer?.mobile_no || salesOrder.customer?.phone) && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Phone / Mobile: <strong>{salesOrder.customer?.mobile_no || salesOrder.customer?.phone}</strong>
                      </Typography>
                      <Tooltip title="Copy Phone Number">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const phone = salesOrder.customer?.mobile_no || salesOrder.customer?.phone || '';
                            if (phone) {
                              navigator.clipboard.writeText(phone);
                              enqueueSnackbar(`Copied ${phone} to clipboard!`, { variant: 'info', autoHideDuration: 2000 });
                            }
                          }}
                          sx={{ p: 0.25 }}
                        >
                          <CopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'none', py: 0, px: 1, minHeight: 24, fontSize: '0.75rem' }}
                        href={`tel:${salesOrder.customer?.mobile_no || salesOrder.customer?.phone}`}
                      >
                        Call
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ textTransform: 'none', py: 0, px: 1, minHeight: 24, fontSize: '0.75rem' }}
                        href={`https://wa.me/${(salesOrder.customer?.mobile_no || salesOrder.customer?.phone || '').replace(/\D/g, '')}`}
                        target="_blank"
                      >
                        WhatsApp
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Addresses */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Addresses
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Billing Address
                  </Typography>
                  {salesOrder.billing_address ? (
                    <Box>
                      <Typography variant="body2">
                        {salesOrder.billing_address.address_line1}
                      </Typography>
                      {salesOrder.billing_address.address_line2 && (
                        <Typography variant="body2">
                          {salesOrder.billing_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {salesOrder.billing_address.city}, {salesOrder.billing_address.state} {salesOrder.billing_address.postal_code}
                      </Typography>
                      {salesOrder.billing_address.country && (
                        <Typography variant="body2">
                          {salesOrder.billing_address.country}
                        </Typography>
                      )}
                      {salesOrder.billing_address.contact_person && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Contact: {salesOrder.billing_address.contact_person}
                        </Typography>
                      )}
                      {salesOrder.billing_address.contact_phone && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {salesOrder.billing_address.contact_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No billing address
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Shipping Address
                  </Typography>
                  {salesOrder.shipping_address ? (
                    <Box>
                      <Typography variant="body2">
                        {salesOrder.shipping_address.address_line1}
                      </Typography>
                      {salesOrder.shipping_address.address_line2 && (
                        <Typography variant="body2">
                          {salesOrder.shipping_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {salesOrder.shipping_address.city}, {salesOrder.shipping_address.state} {salesOrder.shipping_address.postal_code}
                      </Typography>
                      {salesOrder.shipping_address.country && (
                        <Typography variant="body2">
                          {salesOrder.shipping_address.country}
                        </Typography>
                      )}
                      {salesOrder.shipping_address.contact_person && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Contact: {salesOrder.shipping_address.contact_person}
                        </Typography>
                      )}
                      {salesOrder.shipping_address.contact_phone && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {salesOrder.shipping_address.contact_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No shipping address
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Line Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesOrder.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {item.variant?.product?.name || 'No Product'} - {item.variant?.name || 'No Variant'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              SKU: {item.variant?.sku || 'N/A'}
                            </Typography>
                            {item.description && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {item.description}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell align="right">
                          {item.discount_percent > 0 ? `${item.discount_percent}%` : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatCurrency(item.line_total || 0)}
                        </TableCell>
                      </TableRow>
                    ))}                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Notes */}
          <DocumentNotesTimeline notes={salesOrder.notes} />
        </Grid>

        {/* Summary and Actions */}
        <Grid item xs={12} md={4}>
          {/* Order Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal:</Typography>
                <Typography>{formatCurrency(salesOrder.subtotal)}</Typography>
              </Box>
              {salesOrder.shipping_charges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Shipping:</Typography>
                  <Typography>{formatCurrency(salesOrder.shipping_charges)}</Typography>
                </Box>
              )}
              {salesOrder.packing_charges > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Packing:</Typography>
                  <Typography>{formatCurrency(salesOrder.packing_charges)}</Typography>
                </Box>
              )}
              {salesOrder.other_charges.length > 0 && (
                <>
                  {salesOrder.other_charges.map((charge, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" mb={1}>
                      <Typography>{charge.name}:</Typography>
                      <Typography>{formatCurrency(charge.amount)}</Typography>
                    </Box>
                  ))}
                </>
              )}
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Tax:</Typography>
                <Typography>{formatCurrency(salesOrder.tax_amount)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6" color="primary">
                  Total:
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(salesOrder.total_amount)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Razorpay Payment Link Status */}
          {salesOrder.converted_to_invoice_id && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6">Payment Link Status</Typography>
                  <Tooltip title="Refresh status from Razorpay">
                    <IconButton
                      size="small"
                      onClick={handleRefreshPaymentLinkStatus}
                      disabled={razorpayLinkStatusLoading}
                    >
                      {razorpayLinkStatusLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
                {razorpayLinkStatus ? (
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Chip
                        label={razorpayLinkStatus.status.toUpperCase()}
                        color={
                          razorpayLinkStatus.status === 'paid' ? 'success'
                          : razorpayLinkStatus.status === 'expired' || razorpayLinkStatus.status === 'cancelled' ? 'error'
                          : 'warning'
                        }
                        size="small"
                      />
                    </Box>
                    {razorpayLinkStatus.paid_amount !== undefined && (
                      <Typography variant="body2" color="text.secondary">
                        Paid: <strong>{formatCurrency(razorpayLinkStatus.paid_amount)}</strong>
                      </Typography>
                    )}
                    {razorpayLinkStatus.status !== 'paid' && razorpayLinkStatus.status !== 'expired' && razorpayLinkStatus.status !== 'cancelled' && (
                      <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                        Payment link is active but not yet paid.
                      </Alert>
                    )}
                    {razorpayLinkStatus.status === 'expired' && (
                      <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                        Payment link has expired. Generate a new one.
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Click refresh to check Razorpay payment link status.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Order History */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order History
              </Typography>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Created: {format(new Date(salesOrder.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
                {salesOrder.created_by_admin && (
                  <Typography variant="body2" color="text.secondary">
                    By: {salesOrder.created_by_admin.name}
                  </Typography>
                )}
                {salesOrder.status_changed_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status changed: {format(new Date(salesOrder.status_changed_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
                {salesOrder.invoiced_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Invoiced: {format(new Date(salesOrder.invoiced_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Shipment Information */}
          {(salesOrder.status === SalesOrderStatus.CONFIRMED || 
            salesOrder.status === SalesOrderStatus.PROCESSING ||
            salesOrder.status === SalesOrderStatus.READY_TO_SHIP ||
            salesOrder.status === SalesOrderStatus.SHIPPED) && (
            <Box sx={{ mt: 2 }}>
              <ShipmentSection
                orderId={salesOrder.id}
                awbNumber={salesOrder.awb_number}
                trackingUrl={salesOrder.tracking_url}
                shipmentStatus={salesOrder.shipment_status}
                carrierName={salesOrder.carrier_name}
                onShipmentCreated={async () => {
                  // Refresh order data after shipment creation
                  const updatedOrder = await salesOrderService.getSalesOrder(salesOrder.id);
                  setSalesOrder(updatedOrder);
                }}
              />
            </Box>
          )}

          {/* PDF Preview */}
          <Box sx={{ mt: 2 }}>
            <SalesOrderPDFPreview
              salesOrder={salesOrder}
              onDownloadPDF={handleDownloadPDF}
            />
          </Box>
        </Grid>

        {/* Audit History */}
        <Grid item xs={12}>
          <DocumentAuditHistory
            documentId={salesOrder.id}
            documentType="sales_order"
            title="Sales Order Audit History"
          />
        </Grid>
      </Grid>

      {/* Convert to Invoice Dialog */}
      <Dialog open={convertToInvoiceOpen} onClose={() => setConvertToInvoiceOpen(false)}>
        <DialogTitle>Convert to Invoice</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will create an invoice from this sales order.
          </Typography>
          <TextField
            fullWidth
            label="Due Days"
            type="number"
            value={dueDays}
            onChange={(e) => setDueDays(parseInt(e.target.value) || 30)}
            inputProps={{ min: 1, max: 365 }}
            helperText="Number of days from invoice date for payment due date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertToInvoiceOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConvertToInvoice}
            disabled={actionLoading}
          >
            {actionLoading ? 'Converting...' : 'Convert to Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => setStatusUpdateOpen(false)}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          {validStatusTransitions.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No status transitions available from {salesOrder?.status.toUpperCase()}
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Current Status: <strong>{salesOrder?.status.toUpperCase()}</strong>
              </Alert>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>New Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as SalesOrderStatus)}
                  label="New Status"
                >
                  {validStatusTransitions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.replace(/_/g, ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>
            Cancel
          </Button>
          {validStatusTransitions.length > 0 && (
            <Button
              variant="contained"
              onClick={handleStatusUpdate}
              disabled={actionLoading}
            >
              {actionLoading ? 'Updating...' : 'Update Status'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onClose={() => setRecordPaymentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Record Payment — {salesOrder.order_number}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {salesOrder.converted_to_invoice_id ? (
              <Alert severity="info">
                Payment will be recorded against the invoice linked to this Sales Order.
              </Alert>
            ) : (
              <Alert severity="warning">
                No invoice exists yet. Recording this payment will auto-create a draft invoice and record the advance payment against it.
              </Alert>
            )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Amount (₹) *"
                    type="number"
                    value={recordPaymentForm.amount}
                    onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, amount: parseFloat(e.target.value) || 0 })}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Payment Date *"
                    value={recordPaymentForm.payment_date}
                    onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, payment_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Payment Method *"
                    value={recordPaymentForm.payment_method}
                    onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, payment_method: e.target.value })}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="upi">UPI</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer / NEFT / RTGS</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="debit_card">Debit Card</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference / UTR / Cheque #"
                    value={recordPaymentForm.reference_number}
                    onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, reference_number: e.target.value })}
                  />
                </Grid>
              </Grid>
              <TextField
                fullWidth
                label="Bank Name (Optional)"
                value={recordPaymentForm.bank_name}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, bank_name: e.target.value })}
              />
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={recordPaymentForm.notes}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, notes: e.target.value })}
              />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRecordPaymentOpen(false)} disabled={recordPaymentLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmitRecordPayment}
            disabled={recordPaymentLoading || recordPaymentForm.amount <= 0}
            startIcon={recordPaymentLoading ? <CircularProgress size={16} /> : <PaymentIcon />}
          >
            {recordPaymentLoading ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Payment Link Dialog */}
      <Dialog open={paymentLinkOpen} onClose={() => setPaymentLinkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Generate Payment Link — {salesOrder.order_number}
        </DialogTitle>
        <DialogContent dividers>
          {!salesOrder.converted_to_invoice_id ? (
            <Alert severity="info">
              Convert this Sales Order to an Invoice first to generate a Razorpay payment link.
            </Alert>
          ) : (
            <Stack spacing={2.5} sx={{ mt: 1 }}>
              <Alert severity="info">
                A Razorpay payment link will be generated for the invoice linked to this order.
              </Alert>
              <TextField
                select
                fullWidth
                label="Link Expiry Duration"
                value={paymentLinkExpiryHours}
                onChange={(e) => setPaymentLinkExpiryHours(parseInt(e.target.value, 10))}
              >
                <MenuItem value={24}>24 Hours (1 Day)</MenuItem>
                <MenuItem value={48}>48 Hours (2 Days)</MenuItem>
                <MenuItem value={72}>72 Hours (3 Days)</MenuItem>
                <MenuItem value={168}>7 Days (1 Week)</MenuItem>
                <MenuItem value={720}>30 Days (1 Month)</MenuItem>
              </TextField>

              {generatedLinkResult && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.50' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="success.main" gutterBottom>
                    Payment Link Created!
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', mb: 1.5 }}>
                    {generatedLinkResult.payment_link_url || generatedLinkResult.short_url}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CopyIcon />}
                    onClick={() => {
                      navigator.clipboard.writeText(generatedLinkResult.payment_link_url || generatedLinkResult.short_url);
                      enqueueSnackbar('Payment link copied!', { variant: 'info' });
                    }}
                  >
                    Copy Link
                  </Button>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPaymentLinkOpen(false)}>Close</Button>
          {salesOrder.converted_to_invoice_id && !generatedLinkResult && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGeneratePaymentLink}
              disabled={paymentLinkLoading}
              startIcon={paymentLinkLoading ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {paymentLinkLoading ? 'Generating...' : 'Create Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !actionLoading && setCancelDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Cancel Sales Order
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to cancel Sales Order <strong>{salesOrder.order_number}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will release all allocated/reserved inventory back to the warehouse stock and change the order status to <strong>CANCELLED</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setCancelDialogOpen(false)} 
            disabled={actionLoading}
          >
            Go Back
          </Button>
          <Button
            onClick={async () => {
              await handleCancel();
              setCancelDialogOpen(false);
            }}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Cancelling...' : 'Confirm Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesOrderDetail;